import { getScopedDomain } from '../lib/tenantScope';
// Dashboard — six cards in a responsive grid:
//   1. System Information (license/type/FQDN/IP/version + active calls + counts)
//   2. Network Ports
//   3. System Maintenance (updates / backup / storage status)
//   4. Storage usage with progress bars
//   5. Troubleshooting quick actions
//   6. Ops Health (alerts/firmware/MOS/STIR)
// Plus the first-run wizard banner when system is fresh, and an Event Log table.

import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell, Cpu, Activity, ShieldCheck, Sparkles, ChevronRight,
  Server, HardDrive, Wrench, RefreshCw, Database,
  AlertTriangle, AlertCircle, Info, Zap, Phone, Network as NetworkIcon, Wand2,
} from 'lucide-react';
import { api } from '../lib/api';
import { onEvent } from '../lib/ws';
import { LiveSystemCard } from '../components/LiveSystemCard';
import { OnboardingWizard } from '../components/OnboardingWizard';

export default function Dashboard() {
  const me = api.me();
  const tenantId = getScopedDomain(me?.domain_uuid);

  const { data: full, refetch, dataUpdatedAt } = useQuery<any>({
    queryKey: ['dashboard-full'],
    queryFn: () => api.dashboardFull(),
    refetchInterval: 30_000,
  });

  // First-run wizard
  const { data: onboarding, refetch: refetchOnboarding } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => api.onboarding.status(),
  });
  const seedDemo = useMutation({
    mutationFn: () => api.onboarding.demo(),
    onSuccess: (r: any) => {
      alert(`Demo tenant "${r.domain_name}" created.\nWeb login: 1001 / 1002 / 1003 with password "${r.web_login_password}".\n${r.note || ''}`);
      refetchOnboarding(); refetch();
    },
  });

  // Live calls feed via WS
  const [liveCalls, setLiveCalls] = useState<any[]>([]);
  useEffect(() => {
    const off1 = onEvent('admin.call.start', (d) => setLiveCalls(p => [d, ...p].slice(0, 10)));
    const off2 = onEvent('admin.call.end',   (d) => setLiveCalls(p => p.filter(c => c.uuid !== d.uuid)));
    return () => { off1(); off2(); };
  }, []);

  // Wizardify — guided onboarding. Auto-opens once on a fresh system (until dismissed).
  const [wizardOpen, setWizardOpen] = useState(false);
  useEffect(() => {
    if (onboarding?.fresh) { try { if (!localStorage.getItem('wizardify-dismissed')) setWizardOpen(true); } catch {} }
  }, [onboarding?.fresh]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setWizardOpen(true)} className="btn-primary text-xs inline-flex items-center gap-1"><Wand2 size={14} /> Setup Wizard</button>
          <button onClick={() => refetch()} className="btn-ghost text-xs"><RefreshCw size={14} className="inline mr-1"/> Refresh</button>
        </div>
      </div>

      {onboarding?.fresh && <FirstRunBanner steps={onboarding.next_steps} onLaunch={() => setWizardOpen(true)} onSeed={() => confirm('Seed Demo Co tenant?') && seedDemo.mutate()} seeding={seedDemo.isPending} />}

      <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onDone={() => { refetchOnboarding(); refetch(); }}
        serverIp={full?.ipv4?.address}
        fqdn={full?.fqdn}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SystemInformationCard full={full} dataUpdatedAt={dataUpdatedAt} />
        <NetworkPortsCard full={full} />
        <SystemMaintenanceCard full={full} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LiveSystemCard />
        <StorageCard full={full} />
        <TroubleshootingCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <OpsHealthCard tenantId={tenantId} />
      </div>

      <EventLogCard events={full?.events || []} liveCalls={liveCalls} />
    </div>
  );
}

// ── First-run wizard banner ──────────────────────────────────────────────────
function FirstRunBanner({ steps, onSeed, seeding, onLaunch }: { steps: any[]; onSeed: () => void; seeding: boolean; onLaunch: () => void }) {
  return (
    <div className="card p-4 border-2 border-primary/30 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-semibold flex items-center gap-2"><Sparkles className="text-primary" size={18} /> Welcome to Stratus VoIP</h2>
        <div className="flex items-center gap-2">
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={onLaunch}><Wand2 size={14} /> Launch Wizardify</button>
          <button className="btn-ghost text-sm" disabled={seeding} onClick={onSeed}>{seeding ? 'Seeding…' : 'Try Demo'}</button>
        </div>
      </div>
      <p className="text-sm text-textSecondary">New here? Click <strong>Launch Wizardify</strong> for a guided, step-by-step setup — or <strong>Try Demo</strong> to seed a sample tenant. You can also jump straight to a step:</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(steps || []).map((s: any, i: number) => (
          <Link key={s.id} to={s.url} className="card p-2 hover:bg-elevated text-sm flex items-center gap-2">
            <span className="text-primary font-mono">{i + 1}.</span>
            <span className="flex-1">{s.label}</span>
            <ChevronRight size={14} className="text-textSecondary" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── System Information ──────────────────────────────────────────────────────
function SystemInformationCard({ full, dataUpdatedAt }: any) {
  const p = full?.product || {};
  const c = full?.counts || {};
  // Force a re-render every second so the uptime values tick live between
  // server polls. We don't refetch — we just extrapolate from the last
  // server-reported value plus elapsed wall time.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const liveSec = (serverSec: number | null | undefined) =>
    typeof serverSec === 'number' && dataUpdatedAt
      ? serverSec + Math.floor((Date.now() - dataUpdatedAt) / 1000)
      : serverSec ?? null;
  return (
    <div className="card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-2"><Server size={16}/> System Information</h2>
      <Row k="Type" v={p.type || '—'} bold />
      {c.max_calls && <Row k="Capacity" v={`${c.max_calls} Simultaneous Calls`} />}
      <Row k="Partner" v={p.partner || '—'} />
      <Row k="License Owner" v={p.license_owner || '—'} />
      <Row k="Version" v={p.version || '—'} />
      <Row k="FQDN" v={full?.fqdn || '—'} />
      <Row k="Install Type" v={p.install_type || '—'} />
      <Row k="Multi Company" v={full?.multi_company?.enabled ? `Enabled (${full.multi_company.tenants} tenants)` : 'Disabled'} />
      <Row k="IPv4" v={`${full?.ipv4?.address || '—'} ${full?.ipv4?.mode || ''}`} mono />
      <hr className="border-border my-1"/>
      <Row k="System Uptime" v={formatUptime(liveSec(full?.uptime?.host_seconds))} mono />
      <Row k="API Uptime" v={formatUptime(liveSec(full?.uptime?.api_seconds))} mono />
      <Row k="FreeSWITCH Uptime" v={formatUptime(liveSec(full?.pbx?.fs_uptime_seconds))} mono />
      {full?.uptime?.load_avg && (
        <Row k="Load Avg" v={
          <span className="font-mono text-xs">
            {full.uptime.load_avg['1m']?.toFixed(2)} / {full.uptime.load_avg['5m']?.toFixed(2)} / {full.uptime.load_avg['15m']?.toFixed(2)}
          </span>
        } />
      )}
      <hr className="border-border my-1"/>
      <Row k="Active Calls" v={`${c.active_calls ?? 0}${c.max_calls ? ' / ' + c.max_calls : ''}`} bold={c.active_calls > 0} />
      <Row k="Trunks" v={c.trunks ?? 0} />
      <Row k="Custom Templates" v={c.custom_templates ?? 0} />
      <Row k="User / Ext" v={`${c.users_extensions ?? 0} / ${c.system_extensions ?? 0} System`} />
      <Row k="Audit Log" v={full?.audit_log_enabled ? <span className="badge bg-success/20 text-success">Enabled</span> : 'Disabled'} />
    </div>
  );
}

function formatUptime(s: number | null | undefined): string {
  if (s == null || s < 0) return '—';
  const sec = s % 60;
  if (s < 60) return `${sec}s`;
  const min = Math.floor(s / 60) % 60;
  if (s < 3600) return `${min}m ${sec}s`;
  const hour = Math.floor(s / 3600) % 24;
  if (s < 86400) return `${hour}h ${min}m ${sec}s`;
  const days = Math.floor(s / 86400);
  return `${days}d ${hour}h ${min}m ${sec}s`;
}

// ── Network Ports ───────────────────────────────────────────────────────────
function NetworkPortsCard({ full }: any) {
  const p = full?.ports || {};
  return (
    <div className="card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-2"><NetworkIcon size={16}/> Network Ports</h2>
      <Row k="SIP" v={p.sip ?? '—'} mono />
      <Row k="SIPS" v={p.sips ?? '—'} mono />
      <Row k="Tunnel (SBC)" v={p.tunnel ?? '—'} mono />
      <Row k="Media RTP" v={`${p.media_min ?? '—'} – ${p.media_max ?? '—'}`} mono />
      <Row k="HTTP" v={p.http ?? '—'} mono />
      <Row k="HTTPS" v={p.https ?? '—'} mono />
      <hr className="border-border my-1"/>
      <Row k="PBX Engine" v={full?.pbx?.engine || '—'} />
      <Row k="FreeSWITCH" v={<span className="font-mono text-xs">{full?.pbx?.fs_version || 'not running'}</span>} />
    </div>
  );
}

// ── System Maintenance ──────────────────────────────────────────────────────
function SystemMaintenanceCard({ full }: any) {
  const m = full?.maintenance || {};
  const lastBackup = m.last_backup_at ? new Date(m.last_backup_at) : null;
  const backupFresh = lastBackup && (Date.now() - lastBackup.getTime()) < 36 * 3600 * 1000;
  return (
    <div className="card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-2"><Wrench size={16}/> System Maintenance</h2>
      <Row k="Updates" v={m.last_update_check ? `Last checked ${new Date(m.last_update_check).toLocaleString()}` : 'Manual deploy'} />
      <Row k="Auto Updates" v={m.auto_updates ? <span className="badge bg-success/20 text-success">Enabled</span> : <span className="badge">Manual</span>} />
      <Row k="Auto Backup" v={m.auto_backup ? <span className="badge bg-success/20 text-success">{m.backup_schedule}</span> : <span className="badge bg-warning/20 text-warning">Disabled</span>} />
      <Row k="Last Backup" v={lastBackup ? <span className={backupFresh ? 'text-success' : 'text-warning'}>{lastBackup.toLocaleString()}</span> : <span className="text-warning">never</span>} />
      <hr className="border-border my-1"/>
      <div className="flex flex-wrap gap-2 pt-1">
        <Link to="/backup" className="btn-ghost text-xs"><Database size={12} className="inline mr-1"/>Backup</Link>
        <Link to="/system/maintenance" className="btn-ghost text-xs"><Wrench size={12} className="inline mr-1"/>Cleanup</Link>
        <Link to="/system/updates" className="btn-ghost text-xs"><RefreshCw size={12} className="inline mr-1"/>Updates</Link>
      </div>
    </div>
  );
}

// ── Storage ─────────────────────────────────────────────────────────────────
function StorageCard({ full }: any) {
  const s = full?.storage || {};
  return (
    <div className="card p-4 space-y-3">
      <h2 className="font-semibold flex items-center gap-2"><HardDrive size={16}/> Storage</h2>
      <UsageBar label="Disk" used={s.disk?.used} total={s.disk?.avail ? `${s.disk?.used} / ${s.disk?.avail} (${s.disk?.percent})` : null} percent={parseInt(s.disk?.percent, 10) || 0} />
      <UsageBar label="Recordings" used={s.recordings?.size}
                quotaBytes={s.recordings?.quota_bytes} usedBytes={s.recordings?.bytes} />
      <UsageBar label="Voicemail" used={s.voicemail?.size}
                quotaBytes={s.voicemail?.quota_bytes} usedBytes={s.voicemail?.bytes} />
      <Row k="Call Log" v={s.call_log ? `${(s.call_log.rows || 0).toLocaleString()} rows · ${formatBytes(s.call_log.bytes)}` : '—'} />
      <div className="flex gap-2 pt-1">
        <Link to="/recordings" className="btn-ghost text-xs">View Recordings →</Link>
        <Link to="/voicemail" className="btn-ghost text-xs">View Voicemails →</Link>
      </div>
    </div>
  );
}

function UsageBar({ label, used, total, quotaBytes, usedBytes, percent }: any) {
  const pct = percent != null
    ? percent
    : (quotaBytes && usedBytes ? Math.min(100, Math.round((usedBytes / quotaBytes) * 100)) : 0);
  const cls = pct >= 90 ? 'bg-danger' : pct >= 75 ? 'bg-warning' : 'bg-primary';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="font-mono">{total || (quotaBytes ? `${used || '0'} / ${formatBytes(quotaBytes)}` : used || '—')}</span>
      </div>
      <div className="h-1.5 bg-elevated rounded overflow-hidden">
        <div className={cls + ' h-full transition-all'} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Troubleshooting ─────────────────────────────────────────────────────────
function TroubleshootingCard() {
  const restartHost = useMutation({
    mutationFn: () => fetch('/api/troubleshoot/restart-os', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('stratus_token') || ''}` },
      body: JSON.stringify({ confirm: 'YES_RESTART_NOW' }),
    }).then(r => r.json()),
    onSuccess: (r: any) => alert(`Host restart scheduled in ${r.restart_in_seconds || 5} seconds. Stratus will be unreachable for 1-2 minutes.`),
  });
  const restartSvc = useMutation({
    mutationFn: (unit: string) => fetch('/api/troubleshoot/services/restart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('stratus_token') || ''}` },
      body: JSON.stringify({ unit }),
    }).then(r => r.json()),
    onSuccess: (r: any, unit) => alert(r.ok ? `${unit} restarted.` : `Failed: ${r.error || 'unknown'}`),
  });

  const links: Array<{ to: string; label: string; icon?: any }> = [
    { to: '/system/firewall',    label: 'Firewall Test',  icon: ShieldCheck },
    { to: '/system/services',    label: 'Services',       icon: Activity },
    { to: '/audit',              label: 'Audit Log',      icon: ShieldCheck },
    { to: '/activity-log',       label: 'Activity Log',   icon: Activity },
    { to: '/system/maintenance', label: 'Cleanup',        icon: Wrench },
    { to: '/advanced/network',   label: 'IP Config',      icon: NetworkIcon },
    { to: '/firmware-history',   label: 'Firmware',       icon: Cpu },
    { to: '/system/storage',     label: 'Storage',        icon: HardDrive },
  ];

  return (
    <div className="card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-2"><Wrench size={16}/> Troubleshooting</h2>
      <div className="grid grid-cols-2 gap-2">
        {links.map(a => (
          <Link key={a.to} to={a.to} className="btn-ghost text-xs flex items-center gap-1.5 justify-start">
            {a.icon && <a.icon size={12} />}<span>{a.label}</span>
          </Link>
        ))}
      </div>
      <hr className="border-border my-2"/>
      <div className="text-xs text-textSecondary mb-1">Restart services</div>
      <div className="grid grid-cols-2 gap-1">
        {['stratus-api', 'freeswitch', 'kamailio', 'nginx'].map(u => (
          <button key={u} className="btn-ghost text-xs justify-start"
                  disabled={restartSvc.isPending}
                  onClick={() => confirm(`Restart ${u}?`) && restartSvc.mutate(u)}>
            <RefreshCw size={11} className="inline mr-1"/> {u}
          </button>
        ))}
      </div>
      <a className="btn-ghost text-xs w-full inline-flex items-center justify-start mt-1" href="/api/troubleshoot/support-bundle" target="_blank" rel="noreferrer">
        <Database size={11} className="inline mr-1"/> Support bundle (zip)
      </a>
      <button className="btn-ghost text-xs w-full justify-start text-danger"
              disabled={restartHost.isPending}
              onClick={() => prompt('Type RESTART to reboot the entire host (1-2 min downtime):') === 'RESTART' && restartHost.mutate()}>
        <Zap size={11} className="inline mr-1"/> Restart OS (host reboot)
      </button>
    </div>
  );
}

// ── Ops Health ───────────────────────────────────────────────────────────────
function OpsHealthCard({ tenantId }: { tenantId: string | undefined }) {
  const { data: alerts } = useQuery<any>({ queryKey: ['dash-alerts', tenantId], queryFn: () => api.adminNotifications.counts(tenantId), refetchInterval: 30_000, enabled: !!tenantId });
  const { data: atRisk } = useQuery<any>({ queryKey: ['dash-fw', tenantId], queryFn: () => tenantId ? api.firmwareHistory.atRisk({ domain_uuid: tenantId, limit: 100 }) : Promise.resolve([]), refetchInterval: 5*60_000, enabled: !!tenantId });
  const { data: quality } = useQuery<any>({ queryKey: ['dash-mos', tenantId], queryFn: () => tenantId ? api.callLegs.qualityStats({ domain_uuid: tenantId }) : null, refetchInterval: 60_000, enabled: !!tenantId });

  return (
    <div className="card p-4 space-y-2">
      <h2 className="font-semibold flex items-center gap-2"><Activity size={16}/> Ops Health</h2>
      <Stat icon={<Bell size={14}/>} label="Unread alerts" to="/alerts">
        {alerts ? <Badges counts={alerts}/> : '—'}
      </Stat>
      <Stat icon={<Cpu size={14}/>} label="Firmware at risk" to="/firmware-history">{atRisk?.length ?? '—'}</Stat>
      <Stat icon={<Phone size={14}/>} label="Avg MOS (5min)" to="/call-history">
        {quality?.avg_mos_a_to_pbx ?? '—'} {quality?.sample_count ? <span className="text-xs text-textSecondary">over {quality.sample_count} samples</span> : null}
      </Stat>
      <Stat icon={<ShieldCheck size={14}/>} label="STIR/SHAKEN" to="/stir-shaken">
        <span className="text-sm">Per-tenant signing</span>
      </Stat>
    </div>
  );
}
function Badges({ counts }: { counts: any }) {
  return (
    <div className="flex gap-1">
      {(counts.critical || 0) > 0 && <span className="badge bg-danger/20 text-danger">{counts.critical} crit</span>}
      {(counts.error || 0) > 0    && <span className="badge bg-danger/20 text-danger">{counts.error} err</span>}
      {(counts.warn || 0) > 0     && <span className="badge bg-warning/20 text-warning">{counts.warn} warn</span>}
      {!counts.total              && <span className="text-success">all clear</span>}
    </div>
  );
}
function Stat({ icon, label, to, children }: { icon: React.ReactNode; label: string; to?: string; children: React.ReactNode }) {
  const inner = (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-textSecondary">{icon}</span>
      <span className="flex-1">{label}</span>
      <span className="font-medium">{children}</span>
    </div>
  );
  return to ? <Link to={to} className="block py-1 hover:bg-elevated rounded px-1 -mx-1">{inner}</Link> : inner;
}

// ── Event Log ───────────────────────────────────────────────────────────────
function EventLogCard({ events, liveCalls }: { events: any[]; liveCalls: any[] }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold flex items-center gap-2"><AlertTriangle size={16}/> Event Log</h2>
        <Link to="/audit" className="btn-ghost text-xs">Open full log →</Link>
      </div>
      {liveCalls.length > 0 && (
        <div className="mb-3 pb-2 border-b border-border">
          <div className="text-xs text-textSecondary mb-1">Live calls</div>
          {liveCalls.slice(0, 5).map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm py-0.5">
              <span className="badge bg-success/20 text-success">in call</span>
              <span className="font-mono">{c.caller || '—'}</span>
              <ChevronRight size={12} className="text-textSecondary" />
              <span className="font-mono">{c.callee || '—'}</span>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-80">
        <table className="table">
          <thead><tr><th>Severity</th><th>Category</th><th>Detail</th><th>When</th></tr></thead>
          <tbody>
            {events.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-textSecondary">No unread events. 🌤</td></tr>}
            {events.map((e: any, i: number) => (
              <tr key={i} className="hover:bg-elevated">
                <td><Severity s={e.severity} /></td>
                <td><span className="badge text-xs">{e.event_id}</span></td>
                <td className="text-xs">{e.detail}</td>
                <td className="text-xs whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Severity({ s }: { s: string }) {
  if (s === 'critical') return <span className="text-danger inline-flex items-center gap-1 text-xs"><Zap size={12}/> Critical</span>;
  if (s === 'error')    return <span className="text-danger inline-flex items-center gap-1 text-xs"><AlertTriangle size={12}/> Error</span>;
  if (s === 'warn')     return <span className="text-warning inline-flex items-center gap-1 text-xs"><AlertCircle size={12}/> Warning</span>;
  return <span className="text-textSecondary inline-flex items-center gap-1 text-xs"><Info size={12}/> Info</span>;
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Row({ k, v, bold, mono }: { k: string; v: any; bold?: boolean; mono?: boolean }) {
  return (
    <div className="flex justify-between items-baseline gap-3 text-sm py-0.5">
      <span className="text-textSecondary">{k}</span>
      <span className={`${bold ? 'font-semibold' : ''} ${mono ? 'font-mono text-xs' : ''} text-right`}>{v}</span>
    </div>
  );
}
function formatBytes(b: number | undefined): string {
  if (!b) return '0';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
